import { MarketAccordionItem, MarketRow, MarketToggle } from '@market/react';
import React from 'react';
import {
  DomainCardInstance,
  DomainCardReceiptPreferences,
  AutoReceiptSetting,
  getFormFactorLabelKey,
  CardFormFactor,
  isOptedIn,
  findPreferencesForInstance,
} from 'routes/profile/models/ReceiptSettings';
import { useUpdateAutoReceiptSettingMutation } from 'store/query/api-extensions/receiptSettings';
import { useRetrieveCardPreferencesQuery } from 'store/query/api-extensions/receiptAndMarketingPreferences';
import ReceiptDeliveryOptions from './ReceiptDeliveryOptions';
import SearchableLoadMoreList, {
  RowItemProps,
} from 'components/profile/SearchableLoadMoreList';
import { useDispatch } from 'react-redux';
import { openToast, ToastVariant } from 'store/toastSlice';
import { useTranslation } from 'react-i18next';

type CardInstanceRowProps = {
  instance: DomainCardInstance;
  isToggleDisabled: boolean;
  preferences: DomainCardReceiptPreferences[];
};

const CardInstanceRow: React.FC<CardInstanceRowProps> = ({
  instance,
  isToggleDisabled,
  preferences,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [updateAutoReceiptSetting, updateResult] =
    useUpdateAutoReceiptSettingMutation();

  const { data: cardResponse, isFetching: isCardPreferencesFetching } =
    useRetrieveCardPreferencesQuery(instance.id);
  const cardPreferences = cardResponse?.cardPreferences ?? [];

  const pref = findPreferencesForInstance(
    preferences,
    instance.id,
    instance.panSequenceNumber
  );
  const currentlyOptedIn = pref ? isOptedIn(pref.autoReceiptSetting) : false;
  const merchantOverrides = pref?.merchantOverrides ?? [];

  const formFactorLabelKey = getFormFactorLabelKey(instance.formFactor);
  const formFactorLabel = formFactorLabelKey ? t(formFactorLabelKey) : null;
  const isPhysical =
    instance.formFactor === CardFormFactor.PHYSICAL ||
    instance.formFactor === CardFormFactor.FORM_FACTOR_UNKNOWN;

  const displayLabel =
    instance.cardholderName ||
    t('profile.receiptSettings.cardGroup.unknownCardholder');

  const subtextParts = [
    isPhysical
      ? t('profile.receiptSettings.cardGroup.physicalCard')
      : formFactorLabel,
    instance.expiration,
    !instance.isActive && t('profile.receiptSettings.cardGroup.inactive'),
    currentlyOptedIn
      ? t('profile.receiptSettings.cardInstance.optedIn')
      : t('profile.receiptSettings.cardInstance.optedOut'),
  ].filter(Boolean);

  const toggleSetting = (optIn: boolean, merchantId?: string) => {
    updateAutoReceiptSetting({
      cardId: instance.id,
      merchantId,
      panSequenceNumber: instance.panSequenceNumber || undefined,
      setting: optIn
        ? AutoReceiptSetting.AUTO_RECEIPT_OPT_IN
        : AutoReceiptSetting.AUTO_RECEIPT_OPT_OUT,
    })
      .unwrap()
      .then(() => {
        dispatch(
          openToast({
            message: t('common.changeSuccess'),
            variant: ToastVariant.Success,
          })
        );
      })
      .catch(() => {
        dispatch(
          openToast({
            message: t('common.somethingWentWrong.retryable.direct'),
            variant: ToastVariant.Critical,
          })
        );
      });
  };

  const merchantRowData: RowItemProps[] = merchantOverrides.map((override) => ({
    ariaLabelItem: override.merchantDisplayName,
    displayName: override.merchantDisplayName,
    enabled: isOptedIn(override.setting),
    id: override.merchantId,
    imageUrl: override.merchantImageUrl,
  }));

  const accordionName = `person-${instance.id}-${instance.panSequenceNumber}`;

  return (
    <div className="border border-gray-200 rounded-lg mb-4">
      <MarketAccordionItem
        name={accordionName}
        size="medium"
        style={{ boxShadow: 'none' }}
      >
        <div slot="label">
          <span className="font-semibold">{displayLabel}</span>
        </div>
        <p slot="subtext" className="text-sm text-gray-400 my-0">
          {subtextParts.join(' · ')}
        </p>
        <div className="mt-2">
          <MarketRow
            onMarketRowSelected={() => toggleSetting(true)}
            onMarketRowDeselected={() => toggleSetting(false)}
            selected={currentlyOptedIn}
            disabled={isToggleDisabled || updateResult.isLoading}
          >
            <label className="medium-20 my-0 text-gray-500">
              {t('profile.receiptSettings.cardInstance.autoReceipt')}
            </label>
            <MarketToggle
              slot="control"
              role="switch"
              aria-checked={currentlyOptedIn}
              aria-label={`${displayLabel} auto-receipt`}
            />
          </MarketRow>
          <ReceiptDeliveryOptions
            cardPreferences={cardPreferences}
            isRetrievePrefsLoading={isCardPreferencesFetching}
            preferenceCardId={instance.id}
            showHeading={false}
          />
          {currentlyOptedIn && merchantOverrides.length > 0 && (
            <>
              <label className="medium-20 mt-6 mb-2 text-gray-500 block">
                {t('profile.receiptSettings.cardInstance.perMerchantSettings')}
              </label>
              <div className="max-h-80 overflow-y-auto">
                <SearchableLoadMoreList
                  ariaLabel={t(
                    'profile.receiptSettings.merchantPreferences.ariaLabel'
                  )}
                  ariaLabelItemFunc={(businessName: string) =>
                    t('profile.receiptSettings.merchantPreference.ariaLabel', {
                      businessName,
                    })
                  }
                  isLoading={false}
                  onRowToggle={(optIn: boolean, merchantId: string) =>
                    toggleSetting(optIn, merchantId)
                  }
                  rowModels={merchantRowData}
                />
              </div>
            </>
          )}
        </div>
      </MarketAccordionItem>
    </div>
  );
};

export default CardInstanceRow;
